import React from 'react';
import { Trans, useTranslation } from '@steinpilz/react-ux/i10n';

import { useNotification } from '@steinpilz/react-ux/business-app';

import type { CaseSubmissionExpiredResponse } from '@api/generated/apiClient.generated';
import { ApiException } from '@api/generated/apiClient.generated';

import { webAdminBase } from './constants';

export const loginUrl = () => `${webAdminBase}auth/login?returnUrl=${encodeURIComponent(window.location.href)}`;

export const getUrl = (route: string) => `${webAdminBase}${route.startsWith('/') ? route.slice(1) : route}`;

type NotifyFn = ReturnType<typeof useNotification>[0];

let _notify: NotifyFn | null = null;

export const NotificationSetup: React.FC = () => {
  const [notify, contextHolder] = useNotification();
  _notify = notify;
  return <>{contextHolder}</>;
};

export const openSuccessNotification = (message: React.ReactNode) => {
  _notify?.({
    type: 'success',
    key: 'success',
    message,
  });
};

export const openErrorNotification = () => {
  _notify?.({
    type: 'error',
    key: 'error',
    message: <Trans value="DamageDoc.AppErrors.AsyncOperationError.Message" />,
  });
};

export interface ValidationError {
  propertyName: string;
  errorCode: string;
  errorMessage: string;
  args: string[];
}

export const parseValidationError = (e: unknown): ValidationError[] | null => {
  if (!(e instanceof ApiException) || e.status !== 400) return null;
  try {
    const body = JSON.parse(e.response) as { errors?: ValidationError[] };
    return body.errors ?? null;
  } catch {
    return null;
  }
};

export const parseExpiredSubmission = (e: unknown): Required<CaseSubmissionExpiredResponse> | null => {
  if (!(e instanceof ApiException) || e.status !== 410) return null;
  return e.result as Required<CaseSubmissionExpiredResponse>;
};

const ValidationErrorList: React.FC<{ errors: ValidationError[] }> = ({ errors }) => {
  const t = useTranslation();
  return (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {errors.map((e, i) => {
        const propLabel = t(`DamageDoc.Properties.${e.propertyName}`);
        return <li key={i}>{t(`DamageDoc.ValidationErrors.${e.errorCode}`, propLabel, ...e.args)}</li>;
      })}
    </ul>
  );
};

export const openValidationErrorNotification = (errors: ValidationError[]) => {
  _notify?.({
    type: 'error',
    key: 'validation-error',
    message: <ValidationErrorList errors={errors} />,
  });
};
